import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Clock, AlertTriangle, ArrowRight } from 'lucide-react';

const TrialCountdown = () => {
  const { isOnTrial, isTrialExpired, getTrialDaysRemaining } = useSubscription();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOnTrial()) return;

    const calculateTimeLeft = () => {
      const daysRemaining = getTrialDaysRemaining();
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + daysRemaining);
      tomorrow.setHours(23, 59, 59, 999);

      const difference = tomorrow - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

        setTimeLeft({ days, hours, minutes });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [isOnTrial, getTrialDaysRemaining]);

  if (!isOnTrial()) {
    return null;
  }

  const handleUpgradeClick = () => {
    navigate('/app/subscription');
  };

  const getCountdownColor = () => {
    const days = timeLeft.days;
    if (days <= 1) return 'text-red-600 bg-red-50 border-red-200';
    if (days <= 3) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const getCountdownIcon = () => {
    const days = timeLeft.days;
    if (days <= 1) return <AlertTriangle className="w-5 h-5" />;
    return <Clock className="w-5 h-5" />;
  };

  return (
    <div className={`rounded-lg border p-4 mb-6 ${getCountdownColor()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {getCountdownIcon()}
          <div>
            <h3 className="font-semibold text-lg">
              {timeLeft.days <= 1 ? 'Trial Ending Soon!' : 'Free Trial Active'}
            </h3>
            <p className="text-sm opacity-90">
              {timeLeft.days > 0 
                ? `${timeLeft.days} day${timeLeft.days !== 1 ? 's' : ''}, ${timeLeft.hours} hour${timeLeft.hours !== 1 ? 's' : ''} remaining`
                : `${timeLeft.hours} hour${timeLeft.hours !== 1 ? 's' : ''}, ${timeLeft.minutes} minute${timeLeft.minutes !== 1 ? 's' : ''} remaining`
              }
            </p>
          </div>
        </div>
        <button
          onClick={handleUpgradeClick}
          className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 text-sm font-medium"
        >
          <span>Upgrade Now</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      
      {timeLeft.days <= 1 && (
        <div className="mt-3 p-3 bg-white rounded-lg">
          <p className="text-sm font-medium text-gray-700">
            ⚠️ Your free trial is ending soon. Upgrade to continue accessing all features and maintain your progress.
          </p>
        </div>
      )}
    </div>
  );
};

export default TrialCountdown; 